import React, { useMemo, useState } from 'react'
import './dashboard.css'
import AccommodationList from './AccommodationInfoForm';
import ContactInfoForm from './ContactInfoForm';
import StylesForm from './StylesForm';
import TandC from './TandC';
import MetaTagsForm from './MetaTagsForm';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mantine/core';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import "../properties/propertiesLanding.css";
import useAuth from '../../hooks/useAuth';
import ImageContent from '../dynamic/ImageContent';

export default function Dashboard(props) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { username } = useAuth();

  const isSubTenant = useMemo(() => {
    const host = String(window?.location?.hostname || "").trim().toLowerCase();
    const effective = (host && host !== "localhost" && host !== "127.0.0.1")
      ? host
      : String(username || "").trim().toLowerCase();

    const parts = effective.split(".").filter(Boolean);
    return parts.length > 2;
  }, [username]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [requestedCount, setRequestedCount] = useState(1);
  const [propertyNames, setPropertyNames] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [properties, setProperties] = useState([]);

  const mainDomain = useMemo(() => {
    const main = (properties || []).find((p) => p?.isMain);
    return main?.domain || properties?.[0]?.domain || "";
  }, [properties]);

  const openAddModal = async () => {
    setSubmitError("");
    setSubmitSuccess("");
    setAddModalOpen(true);

    if ((properties || []).length > 0) return;
    try {
      const res = await axiosPrivate.get("/properties");
      const list = Array.isArray(res?.data?.properties) ? res.data.properties : [];
      setProperties(list);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load properties";
      setSubmitError(msg);
    }
  };

  const applyCount = () => {
    const n = Math.max(1, Number(requestedCount) || 1);
    setRequestedCount(n);
    setPropertyNames((prev) => {
      const next = [...(prev || [])];
      while (next.length < n) next.push("");
      return next.slice(0, n);
    });
  };

  const addAnotherRow = () => {
    setRequestedCount((prev) => (Number(prev) || 1) + 1);
    setPropertyNames((prev) => [...(prev || []), ""]);
  };

  const removeRow = (idx) => {
    setPropertyNames((prev) => {
      const next = (prev || []).filter((_, i) => i !== idx);
      setRequestedCount(Math.max(1, next.length));
      return next.length ? next : [""];
    });
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    const names = (propertyNames || []).map((n) => String(n || "").trim()).filter(Boolean);
    if (names.length === 0) {
      setSubmitError("At least one property name is required");
      return;
    }
    if (!mainDomain) {
      setSubmitError("Main property is not available yet");
      return;
    }

    setSubmitting(true);
    try {
      await axiosPrivate.post("/properties/request", {
        mainDomain,
        requestedCount: names.length,
        propertyNames: names,
      });
      setSubmitSuccess("Request submitted. You will be notified once it is approved.");
      setRequestedCount(1);
      setPropertyNames([""]);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit property request";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="default">
        <div className="stripe">
          <div className="contact-title">
            <h1>{t("dashboard")}</h1>
            {!isSubTenant && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="properties-landing__actionBtn"
                  onClick={openAddModal}
                >
                  Add New Property
                </button>
              </div>
            )}
          </div>
        </div>

        <Modal
          opened={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          title="Add New Property"
          size="lg"
          centered
        >
          <form onSubmit={submitRequest}>
            <label className="form-label">
              How many properties?
              <input
                type="text"
                value={requestedCount}
                onChange={(e) => setRequestedCount(e.target.value)}
                disabled={submitting}
              />
            </label>

            <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="properties-landing__actionBtn"
                onClick={applyCount}
                disabled={submitting}
              >
                Apply
              </button>
              <button
                type="button"
                className="properties-landing__actionBtn"
                onClick={addAnotherRow}
                disabled={submitting}
              >
                Add another
              </button>
            </div>

            <div style={{ marginTop: 16 }}>
              {propertyNames.map((val, idx) => (
                <div key={idx} style={{ marginBottom: 12 }}>
                  <label className="form-label">
                    Property name {idx + 1}
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPropertyNames((prev) => (prev || []).map((x, i) => (i === idx ? v : x)));
                      }}
                      disabled={submitting}
                    />
                  </label>

                  {propertyNames.length > 1 && (
                    <button
                      type="button"
                      className="properties-landing__actionBtn"
                      onClick={() => removeRow(idx)}
                      disabled={submitting}
                      style={{ marginTop: 6 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>

            {submitError && (
              <div className="properties-landing__status properties-landing__status--error">
                {submitError}
              </div>
            )}

            {submitSuccess && (
              <div className="properties-landing__status properties-landing__status--success">
                {submitSuccess}
              </div>
            )}

            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
              <button
                type="submit"
                className="properties-landing__actionBtn"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </Modal>

        <hr></hr>
        <div className="container">
          <ContactInfoForm />
        </div>
        <hr></hr>
        {isSubTenant && (
          <>
            <div className="container">
              <h2>Destination Card Image</h2>
              <ImageContent
                title="subdomain-card-image"
                username={username}
                type="properties-landing__image"
                smallEditButton={true}
                showPlaceholder={true}
              />
            </div>
            <hr></hr>
          </>
        )}
        <div className="container">
          <AccommodationList />
        </div>
        <hr></hr>
        <div className="container">
          <MetaTagsForm />
        </div>
        <hr></hr>
        <div className="container">
          <TandC />
        </div>
        <hr></hr>
        <div className="container">
          <StylesForm />
        </div>
      </div>
    </>
  );
};

